import { useDarkMode } from '@/hooks/useDarkMode';

import { Checkbox } from '@/components/ui/checkbox';
import SortDropdown from '@/components/SortDropdown';
import SearchBar from '@/components/SearchBar';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';
export default function FilterPanel({
  showFilters,
  setShowFilters,
  filters,
  setFilters,
  categories,
  priceRanges,
  merchants,
  allTags,
  hasActiveFilters,
  clearFilters,
  removeFilter,
  onAIClick,
  sortBy,
  setSortBy,
  products = [],
}) {
  const darkMode = useDarkMode();
  const headingColor = darkMode.isDark ? '#FED7A5' : '#20212B';
  const bodyColor = darkMode.isDark ? '#FFFAF5' : '#20212B';
  // Panel bg sits noticeably lighter than the sticky filter bar backdrop
  // (#2D4354) in dark mode so the card has clear edges and doesn't blend.
  const panelBg = darkMode.isDark ? '#4A6278' : 'white';
  const dividerColor = darkMode.isDark ? '#6B8599' : '#E5E5E5';
  const handleMerchantToggle = (merchant) => {
    setFilters(prev => ({
      ...prev,
      merchant: prev.merchant.includes(merchant)
        ? prev.merchant.filter(m => m !== merchant)
        : [...prev.merchant, merchant]
    }));
  };

  const handleTagToggle = (tag) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  return (
    <>
      {/* Expandable Filter Panel */}
      <div
        className="transition-all duration-300"
        style={{
          maxHeight: showFilters ? '2000px' : '0',
          opacity: showFilters ? 1 : 0,
          overflow: showFilters ? 'visible' : 'hidden'
        }}
      >
        <div 
          className="p-6 rounded-b-xl transition-colors"
          style={{
            background: panelBg,
            boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
            border: darkMode.isDark ? '1px solid #6B8599' : '1px solid #E5E5E5',
            borderTop: 'none',
          }}
        >
          {/* Search input - relocated from top of Browse page. Lives at the
              top of the filter drawer so search + filters read as one
              coherent refinement action. */}
          <div className="mb-5 pb-4 border-b" style={{borderColor: dividerColor}}>
            <h3 className="text-sm font-semibold mb-3" style={{color: headingColor}}>Search</h3>
            <SearchBar
              value={filters.searchQuery}
              onChange={(value) => setFilters({...filters, searchQuery: value})}
              onClear={() => setFilters({...filters, searchQuery: ''})}
              products={products}
              categories={categories}
              tags={allTags}
            />
          </div>

          {/* Sort row - moved into filter panel */}
          <div className="flex items-center justify-between gap-3 mb-5 pb-4 border-b" style={{borderColor: dividerColor}}>
            <h3 className="text-sm font-semibold" style={{color: headingColor}}>Sort By</h3>
            <div data-testid="filter-panel-sort-dropdown">
              <SortDropdown sortBy={sortBy} onChange={setSortBy} />
            </div>
          </div>
          {/* Multi-column grid layout */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
            {/* Category lives in the sticky chip rail above, kept out of
                the FilterPanel to avoid two competing UIs for the same
                filter. Price, Merchant, and Tags remain here. */}

            {/* Price Range */}
            <div>
              <h3 className="text-sm font-semibold mb-3" style={{color: headingColor}}>Price Range</h3>
              <div className="space-y-2">
                {priceRanges.map(range => (
                  <label key={range.value} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      id={`price-${range.value}`}
                      checked={filters.priceRange === range.value}
                      onCheckedChange={() => setFilters({
                        ...filters,
                        priceRange: filters.priceRange === range.value ? '' : range.value
                      })}
                    />
                    <span className="text-sm" style={{color: bodyColor}}>{range.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Merchant */}
            {merchants.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3" style={{color: headingColor}}>Merchant</h3>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                  {merchants.map(merchant => (
                    <label key={merchant} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        id={`merchant-${merchant}`}
                        checked={filters.merchant.includes(merchant)}
                        onCheckedChange={() => handleMerchantToggle(merchant)}
                      />
                      <span className="text-sm" style={{color: bodyColor}}>{merchant}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Tags - takes remaining space or full width if merchants empty */}
            <div className={merchants.length > 0 ? 'col-span-2 md:col-span-1' : 'col-span-2 md:col-span-3 lg:col-span-3'}>
              <h3 className="text-sm font-semibold mb-3" style={{color: headingColor}}>Popular Tags</h3>
              <div className="flex flex-wrap gap-2">
                {allTags.slice(0, 20).map(tag => (
                  <button
                    key={tag}
                    onClick={() => handleTagToggle(tag)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:scale-105 ${
                      filters.tags.includes(tag) ? 'shadow-md' : 'hover:shadow-sm'
                    }`}
                    style={{
                      background: filters.tags.includes(tag) ? '#9E6752' : (darkMode.isDark ? 'rgba(255,255,255,0.08)' : '#F3F4F6'),
                      color: filters.tags.includes(tag) ? 'white' : bodyColor,
                      border: filters.tags.includes(tag) ? 'none' : `2px solid ${darkMode.isDark ? '#3A3B45' : '#D1D5DB'}`
                    }}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom action buttons */}
          <div className="flex flex-col items-center gap-3 pt-4 border-t" style={{borderColor: dividerColor}}>
            {/* AI Button - Centered */}
            <button
              onClick={() => {
                setShowFilters(false);
                onAIClick();
              }}
              className="py-3 px-8 rounded-lg font-medium transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
              style={{background: 'linear-gradient(135deg, #9E6752 0%, #FED7A5 100%)', color: '#20212B'}}
            >
              <Sparkles className="w-5 h-5" />
              <span>Help me choose</span>
            </button>

            {/* Clear All */}
            {hasActiveFilters && (
              <Button
                onClick={() => {
                  clearFilters();
                  setShowFilters(false);
                }}
                variant="outline"
                className="w-auto"
              >
                Clear All Filters
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
